import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import AddSecondaryEmailForm from 'src/components/AddSecondaryEmailPopup/AddSecondaryEmailForm'
import VerifySecondaryEmailForm from 'src/components/AddSecondaryEmailPopup/VerifySecondaryEmailForm'
import Popup from 'src/components/Popup/Popup'
import { ImageNames } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { secondaryEmailsQueryKeys } from 'src/queries/secondaryEmails/secondaryEmails.queries'
import { createSetIsAddSecondaryEmailPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

export type SecondaryEmailFlatFields = {
  email?: string
}

export type SecondaryEmailVerificationFlatFields = {
  verificationCode?: string
}

const AddSecondaryEmailPopup: React.FC = () => {
  const translate = useTranslate()

  // Which step of the popup is shown. Set when the add mutation succeeds; this
  // previously lived in the secondaryEmails Redux slice as `selectedSecondaryEmail`
  // but is pure popup-flow UI state, so it is local now. The popup remounts each
  // time it opens (Authed gates it on isAddSecondaryEmailPopupOpen), so this
  // resets to the Add step on every reopen.
  const [selectedSecondaryEmail, setSelectedSecondaryEmail] = useState<
    string | undefined
  >()
  const buildingsImageSrc = useColorOrGrayscaleImage(ImageNames.Buildings)

  const dispatch = useFlyoutDispatch()
  const queryClient = useQueryClient()

  return (
    <Popup
      noPadding
      onClose={(): void => {
        dispatch(createSetIsAddSecondaryEmailPopupOpen(false))
        // Mirrors the legacy onClose `getSecondaryEmails()` refetch so the
        // profile list reflects anything added/verified while the popup was open.
        queryClient.invalidateQueries({
          queryKey: secondaryEmailsQueryKeys.list(),
        })
      }}
    >
      <div
        css={css`
          position: relative;
          padding: 60px;
        `}
      >
        <img
          src={buildingsImageSrc}
          alt=""
          css={css`
            position: absolute;
            top: 0;
            right: 0;
            border-top-right-radius: 10px;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              display: none;
            `)}
          `}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 20px;
          `}
        >
          {translate('Add a secondary email')}
        </div>
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <div
            css={css`
              margin-bottom: 20px;
            `}
          >
            {translate(
              'Your secondary email address can be used as an additional way to forward receipts. You may not use it on login.',
            )}
          </div>
          {translate(
            "After entering your email, you'll need to confirm the 6-digit verification code we send you. If you don't see it, please check your spam folder.",
          )}
        </div>
        <div
          css={css`
            margin-top: 40px;
            display: flex;
            flex-direction: column;
          `}
        >
          {selectedSecondaryEmail ? (
            <VerifySecondaryEmailForm secondaryEmail={selectedSecondaryEmail} />
          ) : (
            <AddSecondaryEmailForm onAdded={setSelectedSecondaryEmail} />
          )}
        </div>
      </div>
    </Popup>
  )
}

export default AddSecondaryEmailPopup
